import type { SingleBillInfoFormData, SingleBillInfoFormHandlers } from '@ctct/application-layer';
import { Button, Heading } from '@ctct/payflow-ui';
import { Col, Container, Row } from '@ctct/presentation-layer';
import type { SingleBillInfoFormHooks } from '@ctct/state-client-layer';

import './single-bill-info-card.scss';

interface Props {
  data: SingleBillInfoFormData;
  handlers: SingleBillInfoFormHandlers;
  hooks: SingleBillInfoFormHooks;
}

function formatName(firstname, lastname) {
  return `${firstname} ${lastname}`;
}

function formatCityState(city, state, countryRequiresStates = true) {
  if (countryRequiresStates) return `${city}, ${state}`;

  return city;
}

export const SingleBillInfoCard = ({ data, handlers, hooks }: Props) => {
  const isLoading = hooks.useIsLoading();

  const {
    firstname,
    lastname,
    companyname,
    address1,
    address2,
    city,
    zipcode,
    state,
    country,
    phone,
    email,
    partnerContactEmail,
    partnerContactName,
    currency,
    billingDayOfMonth,
    vatNumber,
  } = data.getDefaultValues();

  const taxLabel = data.getTaxLabel(country);

  const countryRequiresStates = data.isShowingStates(country);

  const copies = data.getSingleBillInfoFormWidgetCopy();

  return (
    <Container className="single-bill-info-card-container">
      <Row>
        <Col>
          <Row className="single-bill-info-card-row first-row">
            <Col className="single-bill-info-card-col">
              <Heading level="h6-xs">{copies.billingDayOfMonth.concat(':')}</Heading>
              <p>{billingDayOfMonth}</p>
            </Col>
            <Col className="single-bill-info-card-col">
              <Heading level="h6-xs">{copies.currency.concat(':')}</Heading>
              <p>{currency}</p>
            </Col>
          </Row>
          <Row className="single-bill-info-card-row">
            <Col className="single-bill-info-card-col address-details">
              <Heading level="h6-xs">{copies.addressLine1.concat(':')}</Heading>
              {[
                companyname,
                vatNumber ? `${taxLabel}: ${vatNumber}` : '',
                formatName(firstname, lastname),
                address1,
                address2,
                formatCityState(city, state, countryRequiresStates),
                zipcode,
                data.getCountryName(country),
              ]
                .filter(Boolean)
                .map((el) => (
                  <p key={el}>{el}</p>
                ))}
            </Col>

            <Col className="single-bill-info-card-col contact-details">
              <div>
                <Heading level="h6-xs">{copies.phone.concat(':')}</Heading>
                <p>{phone}</p>
              </div>

              <div>
                <Heading level="h6-xs">{copies.email.concat(':')}</Heading>
                <p className="single-bill-info__email-truncated">{email}</p>
              </div>

              <div>
                <Heading level="h6-xs">{copies.partnerContact.concat(':')}</Heading>
                <p>{partnerContactName}</p>
                <p>{partnerContactEmail}</p>
              </div>
            </Col>
          </Row>
        </Col>
        <Col fixed>
          <Button variant="text" onClick={handlers.onEditClick} disabled={isLoading} busy={isLoading}>
            {copies.edit}
          </Button>
        </Col>
      </Row>
    </Container>
  );
};
